import React, { useState, useCallback, useEffect, useRef } from 'react';
import Cropper from 'react-easy-crop';
import type { Area, Point } from 'react-easy-crop';
import { X, RotateCcw } from 'lucide-react';

export interface CropData {
  x: number;
  y: number;
  width: number;
  height: number;
  zoom: number;
  aspect: number;
  // Pixel-based crop area for rendering
  croppedAreaPixels?: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  // Percentage-based crop area (0-100)
  croppedAreaPercent?: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  // Image effect options
  grayscale?: boolean;
}

interface ImageCropModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageUrl: string;
  initialCrop?: CropData;
  /** When set, the crop window uses this width/height and the aspect picker is hidden. */
  lockedAspect?: number;
  onApply: (cropData: CropData, croppedAreaPixels: Area) => void;
}

const ASPECT_RATIO_OPTIONS = [
  { label: 'Free', value: 0 },
  { label: '1:1 (Square)', value: 1 },
  { label: '4:3', value: 4 / 3 },
  { label: '3:4', value: 3 / 4 },
  { label: '16:9', value: 16 / 9 },
  { label: '9:16', value: 9 / 16 },
  { label: '3:2', value: 3 / 2 },
  { label: '2:3', value: 2 / 3 },
];

export const ImageCropModal: React.FC<ImageCropModalProps> = ({
  isOpen,
  onClose,
  imageUrl,
  initialCrop,
  lockedAspect,
  onApply,
}) => {
  const [crop, setCrop] = useState<Point>({
    x: initialCrop?.x ?? 0,
    y: initialCrop?.y ?? 0,
  });
  const [zoom, setZoom] = useState(initialCrop?.zoom ?? 1);
  const [aspect, setAspect] = useState(lockedAspect ?? initialCrop?.aspect ?? 0);
  const [grayscale, setGrayscale] = useState(initialCrop?.grayscale ?? false);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [croppedAreaPercent, setCroppedAreaPercent] = useState<Area | null>(null);
  const [isMediaReady, setIsMediaReady] = useState(false);
  const cropperRef = useRef<InstanceType<typeof Cropper>>(null);

  const syncCropAreas = useCallback((percent: Area, pixels: Area) => {
    setCroppedAreaPercent(percent);
    setCroppedAreaPixels(pixels);
  }, []);

  // Reset state when modal opens or initialCrop changes
  useEffect(() => {
    if (isOpen) {
      setIsMediaReady(false);
      setCrop({
        x: initialCrop?.x ?? 0,
        y: initialCrop?.y ?? 0,
      });
      setZoom(initialCrop?.zoom ?? 1);
      setAspect(lockedAspect ?? initialCrop?.aspect ?? 0);
      setGrayscale(initialCrop?.grayscale ?? false);
      if (initialCrop?.croppedAreaPixels && initialCrop?.croppedAreaPercent) {
        setCroppedAreaPixels(initialCrop.croppedAreaPixels as Area);
        setCroppedAreaPercent(initialCrop.croppedAreaPercent as Area);
      } else {
        setCroppedAreaPixels(null);
        setCroppedAreaPercent(null);
      }
    }
  }, [isOpen, initialCrop, lockedAspect]);

  const onCropComplete = useCallback(
    (percent: Area, pixels: Area) => {
      syncCropAreas(percent, pixels);
    },
    [syncCropAreas]
  );

  const handleReset = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setAspect(0);
    setGrayscale(false);
    setCroppedAreaPixels(null);
    setCroppedAreaPercent(null);
  };

  const buildCropData = (percent: Area, pixels: Area): CropData => ({
    x: crop.x,
    y: crop.y,
    width: pixels.width,
    height: pixels.height,
    zoom,
    aspect,
    croppedAreaPixels: {
      x: pixels.x,
      y: pixels.y,
      width: pixels.width,
      height: pixels.height,
    },
    croppedAreaPercent: {
      x: percent.x,
      y: percent.y,
      width: percent.width,
      height: percent.height,
    },
    grayscale,
  });

  const finishApply = (percent: Area, pixels: Area) => {
    onApply(buildCropData(percent, pixels), pixels);
    onClose();
  };

  const handleApply = () => {
    // Always recompute from live cropper state (pan, zoom, aspect, grayscale).
    const computed = cropperRef.current?.getCropData();
    if (computed) {
      finishApply(computed.croppedAreaPercentages, computed.croppedAreaPixels);
      return;
    }

    // Fallback only if cropper not ready yet (should be rare — Apply is disabled until loaded).
    if (croppedAreaPercent && croppedAreaPixels) {
      finishApply(croppedAreaPercent, croppedAreaPixels);
      return;
    }

    const percent =
      initialCrop?.croppedAreaPercent ?? ({ x: 0, y: 0, width: 100, height: 100 } as Area);
    const pixels = initialCrop?.croppedAreaPixels;

    if (pixels) {
      finishApply(percent, pixels);
      return;
    }

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      finishApply(percent, {
        x: 0,
        y: 0,
        width: img.naturalWidth || 100,
        height: img.naturalHeight || 100,
      });
    };
    img.onerror = () => {
      finishApply(percent, { x: 0, y: 0, width: 100, height: 100 });
    };
    img.src = imageUrl;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-[100]">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-scroll">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Crop / Adjust Image</h3>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Cropper Area */}
        <div className="relative flex-1 min-h-[400px] bg-gray-900">
          <Cropper
            ref={cropperRef}
            image={imageUrl}
            crop={crop}
            zoom={zoom}
            minZoom={1}
            maxZoom={2}
            aspect={aspect || undefined}
            onCropChange={setCrop}
            onCropComplete={onCropComplete}
            onCropAreaChange={onCropComplete}
            onZoomChange={setZoom}
            onMediaLoaded={() => setIsMediaReady(true)}
            restrictPosition={true}
            showGrid={true}
            style={{
              containerStyle: {
                backgroundColor: '#111827',
              },
              mediaStyle: {
                filter: grayscale ? 'grayscale(100%)' : 'none',
              },
            }}
          />
        </div>

        {/* Controls */}
        <div className="p-4 bg-gray-50 border-t border-gray-200 space-y-4">
          {/* Zoom Slider */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Zoom: {zoom.toFixed(1)}x
            </label>
            <input
              type="range"
              min="1"
              max="2"
              step="0.1"
              value={zoom}
              onChange={e => setZoom(parseFloat(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-600"
            />
          </div>

          {lockedAspect == null && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Aspect Ratio</label>
              <select
                value={aspect}
                onChange={e => setAspect(parseFloat(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              >
                {ASPECT_RATIO_OPTIONS.map(option => (
                  <option key={option.label} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Image Effects */}
          <div className="border-t border-gray-200 pt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Image Effects</label>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={grayscale}
                onChange={e => setGrayscale(e.target.checked)}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">Grayscale (black & white)</span>
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-gray-200">
          <button
            onClick={handleReset}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 flex items-center space-x-2"
          >
            <RotateCcw size={16} />
            <span>Reset</span>
          </button>
          <div className="flex items-center space-x-3">
            <button onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button
              onClick={handleApply}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={!isMediaReady}
            >
              Apply Crop
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
